import React, { useState } from 'react';
import { Mic, MicOff, Send, Square, Loader2 } from 'lucide-react';
import { InterviewStage } from '../../types';
import { STAGE_META } from './interviewConstants';

interface Props {
  stage: InterviewStage;
  micOn: boolean;
  micError: string | null;
  answeredCount: number;
  maxQuestions: number;
  isMock: boolean;
  finishing: boolean;
  onFinish: () => void;
  onSubmitMockAnswer: (text: string) => void;
}

const InterviewControls: React.FC<Props> = ({
  stage,
  micOn,
  micError,
  answeredCount,
  maxQuestions,
  isMock,
  finishing,
  onFinish,
  onSubmitMockAnswer,
}) => {
  const [answer, setAnswer] = useState('');
  const meta = STAGE_META[stage];

  const handleSubmitMock = () => {
    if (!answer.trim() || finishing) return;
    onSubmitMockAnswer(answer);
    setAnswer('');
  };

  return (
    <div className="border-t border-zinc-200 bg-white px-4 py-3 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-full px-3 py-1.5">
          <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
          {meta.label}
        </span>
        <span className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-full px-3 py-1.5">
          {answeredCount} / {maxQuestions} answered
        </span>
        {!isMock && (
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border ${
              micOn ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-zinc-500 bg-zinc-50 border-zinc-200'
            }`}
          >
            {micOn ? <Mic className="w-3 h-3" /> : <MicOff className="w-3 h-3" />}
            {micOn ? 'Mic live' : 'Mic off'}
          </span>
        )}
        <button
          onClick={onFinish}
          disabled={finishing}
          className="h-9 px-4 ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 disabled:opacity-50 rounded-btn transition-colors"
        >
          {finishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
          {finishing ? 'Finishing…' : 'End interview'}
        </button>
      </div>

      {micError && (
        <p className="text-xs text-red-600 bg-red-50 rounded-btn px-3 py-2">{micError}</p>
      )}
      {isMock && stage === 'PROCESSING' && (
        <div className="flex items-center gap-2">
          <input
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmitMock()}
            placeholder="Simulated answer — type it here (mock mode)"
            className="h-10 flex-1 px-3 border border-zinc-200 rounded-btn text-sm text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <button
            onClick={handleSubmitMock}
            disabled={!answer.trim() || finishing}
            className="h-10 px-3 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-brand-500 hover:bg-brand-600 disabled:opacity-50 rounded-btn transition-colors"
          >
            <Send className="w-3.5 h-3.5" /> Send
          </button>
        </div>
      )}
    </div>
  );
};

export default InterviewControls;